import * as Sentry from '@sentry/react';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Outlet } from '@tanstack/react-router';
import { Toaster } from 'react-hot-toast';

import { router } from 'app';

import { AuthProvider } from 'entities/auth';
import { clearCurrentInstallation } from 'entities/installation';

import { queryKeys } from 'shared/api/model';
import { showToast } from 'shared/lib/toast';

let isHandlingInstallation403 = false;
const PUBLIC_ROUTES = ['/auth', '/terms', '/privacy'];
const INSTALLATION_URL_PATTERN = /\/api\/web\/installations\/\d+\//;

const handleApiError = (error: unknown) => {
  if (!(error instanceof Response)) return;

  // Report 4xx (except 401) and 5xx to Sentry
  if (error.status >= 400 && error.status !== 401) {
    Sentry.captureException(new Error(`API ${error.status}: ${error.url}`), {
      extra: { status: error.status, url: error.url },
    });
  }

  // Handle 403 on installation-scoped endpoints
  if (
    error.status === 403 &&
    INSTALLATION_URL_PATTERN.test(error.url) &&
    !isHandlingInstallation403
  ) {
    isHandlingInstallation403 = true;

    clearCurrentInstallation();

    // Cancel in-flight installation-scoped queries, then refetch the installation list
    queryClient.cancelQueries();
    queryClient
      .invalidateQueries({ queryKey: ['installations'] })
      .then(() => {
        router.navigate({ to: '/installations' });
      })
      .finally(() => {
        isHandlingInstallation403 = false;
      });

    showToast({
      title: 'Installation unavailable',
      message: 'Installation is no longer accessible.',
      error: true,
    });
    return;
  }

  if (error.status === 401) {
    // A 401 with no cached session is the expected unauthenticated probe; the route guards redirect.
    const hadSession = !!queryClient.getQueryData(queryKeys.auth.info.queryKey);
    queryClient.setQueryData(queryKeys.auth.info.queryKey, null);
    if (!hadSession || PUBLIC_ROUTES.includes(router.state.location.pathname)) {
      return;
    }

    showToast({
      title: 'Session expired',
      message: 'Please log in again.',
      error: true,
    });
    router.navigate({ to: '/auth' });
  }
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleApiError }),
  mutationCache: new MutationCache({ onError: handleApiError }),
  defaultOptions: {
    queries: {
      networkMode: 'offlineFirst',
      staleTime: 60 * 3600,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Never retry 401/403 — they won't resolve on their own
        if (
          error instanceof Response &&
          (error.status === 401 || error.status === 403)
        ) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

export const RootPage = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <Outlet />
      <Toaster position='top-right' containerStyle={{ top: 72 }} />
      <ReactQueryDevtools initialIsOpen={false} buttonPosition='bottom-right' />
    </AuthProvider>
  </QueryClientProvider>
);
